/* eslint-disable */
import React, { useRef, useCallback, useState, useEffect } from 'react';
import {
  makeStyles,
  Typography,
  Box,
  CircularProgress,IconButton,
  Menu,
  MenuItem,
  RACModalCard,
  RACTextField,
  RACButton,
  InputProps,
} from '@rentacenter/racstrap';
import { MoreVert } from '@material-ui/icons';
import SecondFactor from '../../racstrapComponents/microfrontends/TwoFactorPopup';
import { CustomerNote } from '../../types/noteTypes';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as EditIcon } from '../../assets/images/edit-icon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/images/delete-icon.svg';
import { useUserStateContext } from '../../context/user/user-contexts';
import { getNoteDetails } from '../../api/Customer';

const MAX_NOTE_LENGTH = 1024;
const DEFAULT_ROLE = 'RD';
const MODAL_TITLE = 'Note Details';
const UNKNOWN_AUTHOR = 'Unknown Author';
const JUSTIFY_SPACE_BETWEEN = 'space-between';

const useStyles = makeStyles((theme) => ({
  notesListContainer: {
    flex: 1,
    overflowY: 'hidden',
    marginTop: theme.spacing(1),
    paddingRight: theme.spacing(1),
    minHeight: 0,
    height: '100%',
  },
  noteItem: {
    backgroundColor: 'white',
    marginBottom: theme.spacing(0),
    position: 'relative',
    overflow: 'visible',
  },
  noteItemHeader: {
    display: 'flex',
    justifyContent: JUSTIFY_SPACE_BETWEEN,
    alignItems: 'flex-start',
  },
  menuButton: {
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(-0.5),
  },
  noteDivider: {
    height: 1,
    backgroundColor: '#e0e0e0',
    margin: theme.spacing(2, 1),
  },
  noteHeader: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(1),
  },
  noteCreator: {
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.25),
  },
  noteDate: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  noteContent: {
    fontSize: '0.875rem',
    wordBreak: 'break-word',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.5,
    maxHeight: '4em',
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    marginTop: theme.spacing(1),
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  noNotesMessage: {
    textAlign: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  modalWithoutTitle: {
    '& .MuiDialogTitle-root': {
      display: 'none',
    },
  },
  editInputField: {
    marginBottom: theme.spacing(1),
    '& .MuiInputBase-root': {
      fontSize: '0.9rem',
      borderRadius: 12,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
    },
    '& .MuiOutlinedInput-inputMultiline': {
      padding: theme.spacing(1.5),
      lineHeight: 1.5,
      minHeight: '5em',
    },
    '& .MuiOutlinedInput-multiline': {
      padding: 0,
    },
    '& .MuiFormLabel-root': {
      fontSize: '0.9rem',
      transform: 'translate(14px, 16px) scale(1)',
    },
    '& .MuiFormLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
  characterCount: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textAlign: 'left',
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
}));

interface NotesListProps {
  notes: CustomerNote[];
  loading: boolean;
  hasMoreNotes: boolean;
  onLoadMore: () => void;
  customerId: string;
  onEditNote?: (payload: {
    customerId: string;
    note: string;
    customerNotesId: string;
  }) => void;
  onDeleteNote?: (noteId: string) => void;
  addingNote?: boolean;
}

/**
 * Component that displays a list of notes with infinite scroll
 */
export const NotesList: React.FC<NotesListProps> = ({
  notes = [],
  loading = false,
  hasMoreNotes = false,
  onLoadMore = () => {
    /* No-op fallback */
  },
  customerId,
  onEditNote,
  onDeleteNote,
  addingNote = false,
}) => {
  const classes = useStyles();
  const { user } = useUserStateContext();
  const listRef = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLTextAreaElement>(null);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNote, setSelectedNote] = useState<CustomerNote | null>(null);

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [modalNote, setModalNote] = useState<CustomerNote | null>(null);
  const [noteDetailsLoading, setNoteDetailsLoading] = useState(false);

  // Edit form state
  const [editText, setEditText] = useState('');
  const [isOverLimit, setIsOverLimit] = useState(false);

  // SLA popup states
  const [showEditSLA, setShowEditSLA] = useState(false);
  const [showDeleteSLA, setShowDeleteSLA] = useState(false);
  const [currentRole, setCurrentRole] = useState<string>('');
  const [currentCoworkerId, setCurrentCoworkerId] = useState<string>('');

  // Get current role and coworker ID
  useEffect(() => {
    if (user) {
      setCurrentRole(user.role || '');
      setCurrentCoworkerId(user.employeeId || '');
    }
  }, [user]);

  const formatDate = useCallback(
    (dateStr: string) => dateStr || 'Unknown date',
    []
  );

  const fetchNextPage = useCallback(() => {
    console.log('fetchNextPage called - hasMoreNotes:', hasMoreNotes);
    if (hasMoreNotes) {
      console.log('fetchNextPage calling onLoadMore');
      onLoadMore();
    }
  }, [hasMoreNotes, onLoadMore]);

  const fetchNoteDetails = useCallback(async (customerNoteId: string) => {
    setNoteDetailsLoading(true);
    try {
      const noteDetails = await getNoteDetails(customerNoteId);
      setModalNote(noteDetails);
      setShowViewModal(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch note details:', error);
    } finally {
      setNoteDetailsLoading(false);
    }
  }, []);

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, note: CustomerNote) => {
      if (note.coworkerId === currentCoworkerId) {
        setAnchorEl(event.currentTarget);
        setSelectedNote(note);
      } else {
        fetchNoteDetails(note.customerNoteId);
      }
    },
    [currentCoworkerId, fetchNoteDetails]
  );

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNote(null);
  };

  const handleEdit = () => {
    if (selectedNote) {
      setModalNote(selectedNote);
      setEditText(selectedNote.customerNoteContent || '');
      setIsOverLimit(false);
      setShowEditModal(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedNote) {
      setModalNote(selectedNote);
      setShowDeleteModal(true);
    }
    handleMenuClose();
  };

  // Edit modal handlers
  const handleEditTextChange = (value: string) => {
    if (value.length <= MAX_NOTE_LENGTH) {
      setEditText(value);
      setIsOverLimit(false);
    } else {
      setEditText(value.slice(0, MAX_NOTE_LENGTH));
      setIsOverLimit(true);
    }
  };

  // Handle paste events for edit modal
  const handleEditPaste = () => {
    setTimeout(() => {
      if (textFieldRef.current) {
        const pastedText = textFieldRef.current.value;
        if (pastedText.length > MAX_NOTE_LENGTH) {
          setEditText(pastedText.slice(0, MAX_NOTE_LENGTH));
          setIsOverLimit(true);
        } else {
          setEditText(pastedText);
          setIsOverLimit(false);
        }
      }
    }, 0);
  };

  // Handle undo and redo operations for edit modal
  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    const isUndoRedo =
      (e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'y');

    if (isUndoRedo) {
      setTimeout(() => {
        if (textFieldRef.current) {
          const text = textFieldRef.current.value;
          const isValidLength = text.length <= MAX_NOTE_LENGTH;
          setEditText(isValidLength ? text : text.slice(0, MAX_NOTE_LENGTH));
          setIsOverLimit(!isValidLength);
        }
      }, 0);
    }
  };

  const handleEditSubmit = () => {
    const trimmedText = editText.trim();
    if (!trimmedText || trimmedText.length > MAX_NOTE_LENGTH) return;
    setShowEditModal(false);
    setShowEditSLA(true);
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditText('');
    setIsOverLimit(false);
    setModalNote(null);
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    setModalNote(null);
    setNoteDetailsLoading(false);
  };

  // Delete modal handlers
  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    setShowDeleteSLA(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setModalNote(null);
  };

  // SLA handlers for Edit
  const handleEditSLACompleted = () => {
    setShowEditSLA(false);
    if (modalNote && onEditNote) {
      onEditNote({
        customerId,
        note: editText.trim(),
        customerNotesId: modalNote.customerNoteId,
      });
    }
    setEditText('');
    setIsOverLimit(false);
    setModalNote(null);
  };

  const handleEditSLACancel = () => {
    setShowEditSLA(false);
    setEditText('');
    setIsOverLimit(false);
    setModalNote(null);
  };

  // Render individual note item
  const renderNoteItem = useCallback(
    (note: CustomerNote, index: number) => {
      if (!note || typeof note !== 'object') return null;

      return (
        <React.Fragment key={note.customerNoteId || `note-${index}`}>
          <Box className={classes.noteItem}>
            <div className={classes.noteItemHeader}>
              <div className={classes.noteHeader}>
                <Typography className={classes.noteCreator} variant="body1">
                  {note.coworkerName || UNKNOWN_AUTHOR}
                </Typography>
                <Typography className={classes.noteDate} variant="body2">
                  {formatDate(note.customerNoteDate)}
                </Typography>
              </div>
              <IconButton
                className={classes.menuButton}
                onClick={(e) => handleMenuClick(e, note)}
                size="small"
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </div>
            <Typography className={classes.noteContent} variant="body2">
              {note.customerNoteContent || 'No content'}
            </Typography>
          </Box>
          {index < notes.length - 1 && <div className={classes.noteDivider} />}
        </React.Fragment>
      );
    },
    [classes, notes.length, formatDate, handleMenuClick]
  );

  // Render notes list content
  const renderNotesContent = useCallback(() => {
    if (loading && (!notes || notes.length === 0)) {
      return (
        <div className={classes.loaderContainer}>
          <CircularProgress size={24} />
        </div>
      );
    }

    if (!notes || notes.length === 0) {
      return (
        <Typography className={classes.noNotesMessage} variant="body2">
          No notes available for this customer.
        </Typography>
      );
    }

    return Array.isArray(notes) && notes.map(renderNoteItem);
  }, [loading, notes, classes, renderNoteItem]);

  const renderEndOfNotes = () => {
    if (!hasMoreNotes && notes.length > 0) {
      return (
        <Typography
          align="center"
          variant="body2"
          color="textSecondary"
          style={{ padding: '8px' }}
        >
          — End of Notes —
        </Typography>
      );
    }
    return null;
  };
  const handleDeleteSLACompleted = async () => {
    setShowDeleteSLA(false);
    if (modalNote && onDeleteNote) {
      try {
        onDeleteNote(modalNote.customerNoteId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete note:', error);
      }
    }
    setModalNote(null);
  };

  const handleDeleteSLACancel = () => {
    setShowDeleteSLA(false);
    setModalNote(null);
  };

  // Input props for the edit TextField - same as AddNote
  const inputProps: Partial<InputProps> = {
    inputProps: { maxLength: MAX_NOTE_LENGTH },
    inputRef: textFieldRef,
  };

  return (
    <div
      className={classes.notesListContainer}
      ref={listRef}
      id="notesScrollContainer"
      style={{
        isolation: 'isolate',
        overflowY: 'auto',
      }}
    >
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={notes?.length || 0}
        hasMore={!!hasMoreNotes}
        scrollableTarget="notesScrollContainer"
        loader={
          <div className={classes.loaderContainer}>
            <CircularProgress size={24} />
          </div>
        }
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#888 #f1f1f1',
        } as React.CSSProperties}
      >
        {renderNotesContent()}
        {renderEndOfNotes()}
      </InfiniteScroll>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disablePortal={false}
        container={document.body}
        MenuListProps={{
          'data-notes-drawer-content': 'true',
        } as any}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon
            style={{
              width: '16px',
              height: '16px',
              marginRight: '8px',
              color: '#1976d2',
            }}
          />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon
            style={{
              width: '16px',
              height: '16px',
              marginRight: '8px',
              color: '#d32f2f',
            }}
          />
          Delete
        </MenuItem>
      </Menu>

      {showEditModal && (
        <RACModalCard
          isOpen={showEditModal}
          onClose={handleEditCancel}
          maxWidth="sm"
          borderRadius="20px"
          disableBackdropClick
          disableEscapeKeyDown
          classes={{ dialog: classes.modalWithoutTitle }}
          BackdropProps={{
            'data-notes-drawer-content': 'true',
          } as any}
        >
          <div style={{ padding: '8px 16px 12px 16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: JUSTIFY_SPACE_BETWEEN,
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <Typography
                style={{ fontSize: '20px', fontWeight: 600, color: '#000' }}
              >
                {MODAL_TITLE}
              </Typography>
              <RACButton
                onClick={handleEditCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  padding: '0',
                  color: '#666',
                  minWidth: 'auto',
                  width: '24px',
                  height: '24px',
                }}
              >
                ×
              </RACButton>
            </div>

            <Typography
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#333',
                marginBottom: '4px',
              }}
            >
              {modalNote?.coworkerName || UNKNOWN_AUTHOR}
            </Typography>

            <Typography
              style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}
            >
              {formatDate(modalNote?.customerNoteDate || '')}
            </Typography>

            <Typography
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#333',
                marginBottom: '6px',
              }}
            >
              Notes
            </Typography>

            <RACTextField
              className={classes.editInputField}
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              value={editText}
              onChange={handleEditTextChange}
              onPaste={handleEditPaste}
              onKeyDown={handleEditKeyDown}
              placeholder="Edit your note..."
              error={isOverLimit}
              InputProps={inputProps}
            />

            <Typography className={classes.characterCount}>
              {`(${MAX_NOTE_LENGTH}/${editText.length})`}
            </Typography>

            <div
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',
                marginTop: '12px',
              }}
            >
              <RACButton variant="outlined" onClick={handleEditCancel}>
                Cancel
              </RACButton>
              <RACButton
                variant="contained"
                color="primary"
                onClick={handleEditSubmit}
                disabled={!editText.trim() || isOverLimit}
              >
                Update
              </RACButton>
            </div>
          </div>
        </RACModalCard>
      )}

      {showDeleteModal && (
        <RACModalCard
          isOpen={showDeleteModal}
          onClose={handleDeleteCancel}
          maxWidth="xs"
          borderRadius="8px"
          disableBackdropClick
          disableEscapeKeyDown
          classes={{ dialog: classes.modalWithoutTitle }}
          BackdropProps={{
            'data-notes-drawer-content': 'true',
          } as any}
        >
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <Typography
              style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#333',
                marginBottom: '16px',
              }}
            >
              Delete Note
            </Typography>

            <Typography
              style={{ fontSize: '14px', color: '#666', marginBottom: '24px' }}
            >
              Please be aware once you delete you will not be able to recover
              this note in the future.
            </Typography>

            <div
              style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}
            >
              <RACButton
                variant="outlined"
                onClick={handleDeleteCancel}
                style={{ minWidth: '80px' }}
              >
                Cancel
              </RACButton>
              <RACButton
                variant="contained"
                color="primary"
                onClick={handleDeleteConfirm}
                style={{ minWidth: '80px' }}
              >
                Continue
              </RACButton>
            </div>
          </div>
        </RACModalCard>
      )}

      {showEditSLA && (
        <SecondFactor
          setTwoFactorCompleted={handleEditSLACompleted}
          setTwoFactorCancelClick={handleEditSLACancel}
          CurrentRole={currentRole || DEFAULT_ROLE}
          moduleName="CustomerRetention"
        />
      )}

      {showDeleteSLA && (
        <SecondFactor
          setTwoFactorCompleted={handleDeleteSLACompleted}
          setTwoFactorCancelClick={handleDeleteSLACancel}
          CurrentRole={currentRole || DEFAULT_ROLE}
          moduleName="CustomerRetention"
        />
      )}

      {(noteDetailsLoading || addingNote) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={24} />
        </div>
      )}

      {showViewModal && (
        <RACModalCard
          isOpen={showViewModal}
          onClose={handleViewModalClose}
          maxWidth="sm"
          borderRadius="20px"
          disableBackdropClick
          disableEscapeKeyDown
          classes={{ dialog: classes.modalWithoutTitle }}
          BackdropProps={{
            'data-notes-drawer-content': 'true',
          } as any}
        >
          <div style={{ padding: '8px 16px 12px 16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: JUSTIFY_SPACE_BETWEEN,
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <Typography
                style={{ fontSize: '20px', fontWeight: 600, color: '#000' }}
              >
                {MODAL_TITLE}
              </Typography>
              <RACButton
                onClick={handleViewModalClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  padding: '0',
                  color: '#666',
                  minWidth: 'auto',
                  width: '30px',
                  height: '30px',
                }}
              >
                ×
              </RACButton>
            </div>

            <Typography
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#333',
                marginBottom: '4px',
              }}
            >
              {modalNote?.coworkerName || UNKNOWN_AUTHOR}
            </Typography>

            <Typography
              style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}
            >
              {formatDate(modalNote?.customerNoteDate)}
            </Typography>

            <Typography
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#333',
                marginBottom: '6px',
              }}
            >
              Notes
            </Typography>

            <div
              style={{
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '8px',
                fontSize: '14px',
                lineHeight: 1.5,
                minHeight: '80px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {modalNote?.customerNoteContent || 'No content'}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '12px',
              }}
            >
              <RACButton variant="outlined" onClick={handleViewModalClose}>
                Close
              </RACButton>
            </div>
          </div>
        </RACModalCard>
      )}
    </div>
  );
};
